/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';
import type { AgencyDashboardItem } from './ManageAgenciesCards';
import type { AgencyStatus } from '@/features/onboarding/types/agency-dashboard';

/** What each inactive state asks the owner to do. */
const CTA_KEYS: Partial<Record<AgencyStatus, string>> = {
  no_subscription: 'workspace.pending.ctaActivate',
  payment_failed: 'workspace.pending.ctaPay',
  suspended: 'workspace.pending.ctaPay',
  cancelled: 'workspace.pending.ctaReactivate',
};

/*
 * How many rows the list shows before it folds. Past this the attention list
 * pushes the running offices off the screen, and the grid below it is what
 * most visits came for.
 */
const VISIBLE_LIMIT = 4;

type PendingAgenciesProps = {
  agencies: AgencyDashboardItem[];
  onOpen: (agency: AgencyDashboardItem) => void;
  /** Offered on drafts only, which are the only agencies that can be dropped. */
  onDelete: (agency: AgencyDashboardItem) => void;
};

/**
 * The offices that need something before they can be opened, above the ones
 * that can.
 *
 * They used to sit behind a "Pendentes" tab, which opened closed: the
 * workspace landed on "Ativas" and an office stuck without a plan was a tab
 * away from being noticed at all. The order is the point — what needs you,
 * then what is running.
 *
 * @param props - The pending offices and what the row's actions do.
 * @returns The attention list, or null when nothing is pending.
 */
export function PendingAgencies({ agencies, onOpen, onDelete }: PendingAgenciesProps) {
  const { t } = useTranslation('@pages/agencies');
  const [expanded, setExpanded] = useState(false);

  if (agencies.length === 0) return null;

  const hidden = agencies.length - VISIBLE_LIMIT;
  const shown = expanded ? agencies : agencies.slice(0, VISIBLE_LIMIT);

  return (
    <section data-testid="workspace-pending" className="mb-(--space-8) flex flex-col gap-(--space-3)">
      <Text as="h2" className="m-0 text-base [font-weight:var(--font-bold)] text-(--text-primary)">
        {t('workspace.pending.title', { count: agencies.length })}
      </Text>

      <div
        className="flex flex-col rounded-(--radius-lg) border border-(--border-subtle) overflow-hidden"
        style={{ background: 'var(--surface-raised)' }}
      >
        {shown.map((agency, index) => (
          <PendingRow
            key={agency.id}
            agency={agency}
            divided={index > 0}
            onOpen={() => onOpen(agency)}
            onDelete={() => onDelete(agency)}
          />
        ))}

        {hidden > 0 && (
          <Button
            variant="link"
            size="sm"
            data-testid="workspace-pending-more"
            onClick={() => setExpanded((open) => !open)}
            className="p-(--space-3)"
            style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--seed-base)' }}
          >
            {expanded ? t('workspace.pending.showLess') : t('workspace.pending.showMore', { count: hidden })}
          </Button>
        )}
      </div>
    </section>
  );
}

type PendingRowProps = {
  agency: AgencyDashboardItem;
  divided: boolean;
  onOpen: () => void;
  onDelete: () => void;
};

/**
 * One office waiting on its owner: who it is, what state it is in, and the
 * single action that moves it on.
 *
 * @param props - The office and its actions.
 * @returns The row.
 */
function PendingRow({ agency, divided, onOpen, onDelete }: PendingRowProps) {
  const { t } = useTranslation('@pages/agencies');
  /* An office the list only ever holds because it is not active: a missing
     status is a draft that never reached a subscription. */
  const status = agency.status ?? 'no_subscription';
  const isDraft = status === 'no_subscription';
  const ctaKey = CTA_KEYS[status] ?? 'workspace.access';

  return (
    <div
      data-testid="workspace-pending-row"
      /* Stacked until there is room for two columns: side by side on a phone
         the name was squeezed to a word per line and the actions landed on
         top of the badge. */
      className={cn(
        'flex flex-col items-stretch gap-(--space-3) p-(--space-4)',
        'sm:flex-row sm:items-center',
      )}
      style={divided ? { borderTop: '1px solid var(--border-subtle)' } : undefined}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-(--space-1)">
        <div className="flex flex-wrap items-center gap-(--space-2)">
          <Text as="span" className="min-w-0 text-base [font-weight:var(--font-semibold)] text-(--text-primary)">
            {agency.name}
          </Text>
          <SubscriptionStatusBadge status={status} />
        </div>
        {agency.city && (
          <Text as="span" className="text-sm text-(--text-tertiary)">{agency.city}</Text>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-end gap-(--space-3)">
        {isDraft && (
          <Button
            variant="link"
            size="sm"
            data-testid="workspace-pending-delete"
            onClick={onDelete}
            className="shrink-0"
            style={{ color: 'var(--text-tertiary)' }}
          >
            {t('workspace.pending.delete')}
          </Button>
        )}
        <Button
          data-testid="workspace-pending-cta"
          onClick={onOpen}
          className={cn(
            'min-h-[44px] flex-1 px-(--space-5) sm:flex-none',
            'rounded-full [font-weight:var(--font-semibold)]',
            'bg-[var(--button-primary-bg)] hover:bg-[var(--button-primary-hover-bg)] text-[var(--button-primary-text)]',
          )}
        >
          {t(ctaKey)}
        </Button>
      </div>
    </div>
  );
}
